import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
  Activity, 
  Server, 
  Cpu, 
  HardDrive, 
  MemoryStick,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Power,
  Shield,
  Database,
  Globe,
  Zap,
  BarChart3,
  Eye,
  Settings
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';
import Chart from '../../components/ui/Chart';

const SystemMonitoring = () => {
  const navigate = useNavigate();
  const { getMockData } = useDataContext();
  const [systemHealth, setSystemHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadSystemData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const data = getMockData('systemHealth');
      setSystemHealth(data);
      setAlerts(data.alerts || []);
    } catch (error) {
      console.error('Failed to load system data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [getMockData]);

  useEffect(() => {
    loadSystemData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadSystemData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, loadSystemData]);

  const handleRefresh = () => {
    loadSystemData();
  };

  const handleEmergencyAction = (action) => {
    // Simulate emergency action
    alert(`Emergency action: ${action} initiated`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'green';
      case 'warning': return 'yellow';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'critical': return AlertTriangle;
      default: return Activity;
    }
  };

  const systemMetrics = [
    {
      title: 'CPU Usage',
      value: `${systemHealth?.cpu?.usage || 0}%`,
      status: systemHealth?.cpu?.status || 'healthy',
      icon: Cpu,
      trend: systemHealth?.cpu?.trend || 'stable',
      details: `${systemHealth?.cpu?.cores || 8} cores`
    },
    {
      title: 'Memory Usage',
      value: `${systemHealth?.memory?.usage || 0}%`,
      status: systemHealth?.memory?.status || 'healthy',
      icon: MemoryStick,
      trend: systemHealth?.memory?.trend || 'stable',
      details: `${systemHealth?.memory?.used || 0}GB / ${systemHealth?.memory?.total || 0}GB`
    },
    {
      title: 'Disk Usage',
      value: `${systemHealth?.disk?.usage || 0}%`,
      status: systemHealth?.disk?.status || 'healthy',
      icon: HardDrive,
      trend: systemHealth?.disk?.trend || 'stable',
      details: `${systemHealth?.disk?.used || 0}GB / ${systemHealth?.disk?.total || 0}GB`
    },
    {
      title: 'Network I/O',
      value: `${systemHealth?.network?.throughput || 0} Mbps`,
      status: systemHealth?.network?.status || 'healthy',
      icon: Globe,
      trend: systemHealth?.network?.trend || 'stable',
      details: `In: ${systemHealth?.network?.inbound || 0} Mbps, Out: ${systemHealth?.network?.outbound || 0} Mbps`
    }
  ];

  const serviceMetrics = [
    {
      name: 'Web Server',
      status: systemHealth?.services?.webServer || 'healthy',
      uptime: '99.9%',
      responseTime: '45ms',
      requests: '1,234/min'
    },
    {
      name: 'Database',
      status: systemHealth?.services?.database || 'healthy',
      uptime: '99.8%',
      responseTime: '12ms',
      connections: '45/100'
    },
    {
      name: 'Cache Server',
      status: systemHealth?.services?.cache || 'warning',
      uptime: '98.5%',
      responseTime: '8ms',
      hitRate: '94%'
    },
    {
      name: 'Message Queue',
      status: systemHealth?.services?.messageQueue || 'healthy',
      uptime: '99.7%',
      responseTime: '23ms',
      queueSize: '12'
    }
  ];

  const performanceData = [
    { name: '00:00', cpu: 25, memory: 45, disk: 60 },
    { name: '04:00', cpu: 30, memory: 48, disk: 62 },
    { name: '08:00', cpu: 65, memory: 52, disk: 64 },
    { name: '12:00', cpu: 80, memory: 58, disk: 66 },
    { name: '16:00', cpu: 75, memory: 55, disk: 68 },
    { name: '20:00', cpu: 45, memory: 50, disk: 70 }
  ];

  return (
    <div className="system-monitoring p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Monitoring</h1>
            <p className="text-gray-600">Real-time system health and performance metrics</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="autoRefresh" className="text-sm text-gray-600">Auto-refresh</label>
            </div>
            <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
              {isRefreshing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* System Status Overview */}
      <div className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              System Status Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {systemMetrics.map((metric, index) => {
                const Icon = metric.icon;
                const StatusIcon = getStatusIcon(metric.status);
                return (
                  <div key={index} className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Icon className="h-8 w-8 text-gray-600 mr-2" />
                      <StatusIcon className={`h-6 w-6 text-${getStatusColor(metric.status)}-500`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{metric.title}</h3>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{metric.value}</p>
                    <p className="text-sm text-gray-600">{metric.details}</p>
                    <div className="flex items-center justify-center mt-2">
                      {metric.trend === 'up' && <TrendingUp className="h-4 w-4 text-red-500" />}
                      {metric.trend === 'down' && <TrendingDown className="h-4 w-4 text-green-500" />}
                      {metric.trend === 'stable' && <div className="h-4 w-4 bg-gray-400 rounded-full" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Performance Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Performance Trends (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart 
              type="line" 
              data={performanceData} 
              height={300}
              colors={['#3B82F6', '#10B981', '#F59E0B']}
            />
          </CardContent>
        </Card>

        {/* Service Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Server className="h-5 w-5 mr-2" />
              Service Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {serviceMetrics.map((service, index) => {
                const StatusIcon = getStatusIcon(service.status);
                return (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <StatusIcon className={`h-5 w-5 text-${getStatusColor(service.status)}-500`} />
                      <div>
                        <h3 className="font-semibold">{service.name}</h3>
                        <p className="text-sm text-gray-600">Uptime: {service.uptime}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{service.responseTime}</p>
                      <p className="text-xs text-gray-500">
                        {service.requests || service.connections || service.hitRate || service.queueSize}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts and Emergency Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Active Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Active Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.slice(0, 5).map((alert, index) => (
                <div key={index} className={`p-3 rounded-lg border-l-4 ${
                  alert.severity === 'critical' ? 'border-red-500 bg-red-50' :
                  alert.severity === 'warning' ? 'border-yellow-500 bg-yellow-50' :
                  'border-blue-500 bg-blue-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                      <p className="text-sm text-gray-600">{alert.description}</p>
                    </div>
                    <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                      {alert.severity}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {new Date(alert.timestamp).toLocaleString()}
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate(`/super-admin/alerts/${alert.id}`)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="outline"
                onClick={() => navigate('/super-admin/alerts')}
              >
                View All Alerts
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Emergency Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Zap className="h-5 w-5 mr-2" />
              Emergency Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={() => handleEmergencyAction('Restart Services')}
              >
                <Power className="h-4 w-4 mr-2" />
                Restart All Services
              </Button>
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={() => handleEmergencyAction('Clear Cache')}
              >
                <Database className="h-4 w-4 mr-2" />
                Clear System Cache
              </Button>
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={() => handleEmergencyAction('Maintenance Mode')}
              >
                <Shield className="h-4 w-4 mr-2" />
                Enable Maintenance Mode
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => handleEmergencyAction('Generate Report')}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Generate System Report
              </Button>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                <p className="text-sm text-yellow-800">
                  Emergency actions require confirmation and may cause service interruption.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Server Details</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Hostname: {systemHealth?.server?.hostname || 'server-01'}</p>
                <p>OS: {systemHealth?.server?.os || 'Ubuntu 20.04 LTS'}</p>
                <p>Kernel: {systemHealth?.server?.kernel || '5.4.0-74-generic'}</p>
                <p>Architecture: {systemHealth?.server?.architecture || 'x86_64'}</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Application Info</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Version: {systemHealth?.application?.version || '2.1.0'}</p>
                <p>Build: {systemHealth?.application?.build || '20250115'}</p>
                <p>Uptime: {systemHealth?.application?.uptime || '15 days, 3 hours'}</p>
                <p>Last Restart: {systemHealth?.application?.lastRestart || '2025-01-01 10:30:00'}</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Database Info</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Type: {systemHealth?.database?.type || 'PostgreSQL'}</p>
                <p>Version: {systemHealth?.database?.version || '13.7'}</p>
                <p>Size: {systemHealth?.database?.size || '2.3 GB'}</p>
                <p>Connections: {systemHealth?.database?.connections || '45/100'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemMonitoring;
